import { useForm, Link } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatUnknownError } from "@/lib/utils";
import { Beer, Fingerprint } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { useState } from "react";

type Props = {
  flash: {
    alert: string | null;
    notice: string | null;
  };
};

New.layout = (page: React.ReactNode) => page;

export default function New({ flash }: Props) {
  const form = useForm({ email: "", password: "" });
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.post("/session");
  }

  async function handlePasskeyLogin() {
    setPasskeyError(null);
    setPasskeyLoading(true);

    try {
      const optionsRes = await fetch("/passkey_session/new");
      const options = await optionsRes.json();

      const credential = await startAuthentication({ optionsJSON: options });

      const verifyRes = await fetch("/passkey_session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content ?? "",
        },
        body: JSON.stringify({ credential }),
      });

      const result = await verifyRes.json();

      if (verifyRes.ok && result.redirect_to) {
        window.location.href = result.redirect_to;
      } else {
        setPasskeyError(result.error || "Passkey authentication failed.");
      }
    } catch (err) {
      setPasskeyError(formatUnknownError(err));
    } finally {
      setPasskeyLoading(false);
    }
  }

  const errorMessage = flash.alert || passkeyError;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <Link href="/" className="flex flex-col items-center gap-2">
          <Beer className="size-8 text-foreground" />
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            BoozeHound
          </h1>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>
              Enter your credentials to access your account.
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {errorMessage && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorMessage}
                </div>
              )}

              {flash.notice && (
                <div className="rounded-md bg-green-500/10 px-3 py-2 text-sm text-green-700">
                  {flash.notice}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={form.data.password}
                  onChange={(e) => form.setData("password", e.target.value)}
                  autoComplete="current-password"
                  maxLength={72}
                  required
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-3">
              <Button
                type="submit"
                className="w-full"
                disabled={form.processing}
              >
                {form.processing ? "Signing in…" : "Sign in"}
              </Button>

              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading}
              >
                <Fingerprint className="size-4" />
                {passkeyLoading ? "Waiting for passkey…" : "Sign in with a passkey"}
              </Button>
            </CardFooter>
          </form>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          <Link
            href="/passwords/new"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Forgot your password?
          </Link>
        </p>
      </div>
    </div>
  );
}
